import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { QuickAddConflictKind } from "../../utils/scheduleConflict";
import { WarningDialog } from "../../utils/warningDialogs/WarningDialog";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";
import { QuickAddNurseForm } from "./QuickAddNurseForm";
import { useQuickAddAdd } from "./useQuickAddAdd";
import { useQuickAddMove } from "./useQuickAddMove";
import { useQuickAddPick } from "./useQuickAddPick";
import { useQuickAddSave } from "./useQuickAddSave";
import { useQuickAddScheduleConflict } from "./useQuickAddScheduleConflict";
import { useQuickAddSelection } from "./useQuickAddSelection";
import { useQuickAddTimes } from "./useQuickAddTimes";

type QuickAddNurseDialogProps = {
	date: DateTime;
	showActual: boolean;
	sectionName: string;
	sectionLocationKey: string;
	selectedTimeRange?: { start: DateTime; end: DateTime };
	onClose: () => void;
};

export const QuickAddNurseDialog: FC<QuickAddNurseDialogProps> = ({ date, showActual, sectionName, sectionLocationKey, selectedTimeRange, onClose }) => {
	const { t } = useTranslation();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const selection = useQuickAddSelection(sectionLocationKey);
	const { nurse } = selection;
	const times = useQuickAddTimes({ date, selectedTimeRange });
	const { window, seededWindow, sectionWindow } = times;
	const section = useMemo(() => ({ name: sectionName, locationKey: sectionLocationKey }), [sectionName, sectionLocationKey]);
	const day = { date, showActual };
	const scheduleConflict = useQuickAddScheduleConflict({ ...day, nurse, window, locationFor: selection.locationFor });
	const { shiftsByNurse, location, conflict, hint, isBlocked } = scheduleConflict;
	const movingShift = conflict.kind === QuickAddConflictKind.Move ? conflict.existing : undefined;
	const { jobId, job } = selection.jobFor(movingShift);
	const isLocationMissing = selection.locationOptions.length > 0 && location === null;
	const pickWindows = useMemo(() => ({ currentWindow: window, seededWindow, sectionWindow }), [window, seededWindow, sectionWindow]);
	const pick = useQuickAddPick({ shiftsByNurse, applyPick: selection.applyPick, windows: pickWindows, seedWindow: times.seedWindow });
	const { performAdd, isAdding } = useQuickAddAdd({ ...day, nurse, location, window, job, section, onClose });
	const { performMove, isMoving } = useQuickAddMove({ nurse, location, window, job, jobTouched: selection.jobTouched, section, onClose });
	const { handleSave, warningDialogProps } = useQuickAddSave({ ...day, nurse, location, window, movingShift, performAdd, performMove });
	// A move needs a destination; with no location to pick (a department without any), there is nowhere to move to.
	const isMoveWithoutDestination = movingShift !== undefined && location === null;

	return (
		<>
			<Dialog
				open
				onClose={onClose}
				maxWidth={false}
				data-testid="quick-add-nurse-dialog"
				slotProps={{ paper: { sx: { borderRadius: 2, width: { xs: "50%", xl: "30%" }, height: "auto" } } }}
			>
				<DialogTitle sx={{ pb: 1 }}>
					<Typography sx={(theme) => ({ fontWeight: 600, fontSize: "16px", color: theme.palette.primary.main })}>{t("add.staff")}</Typography>
				</DialogTitle>
				<DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
					<QuickAddNurseForm
						staffOptions={pick.staffOptions}
						staffOption={selection.staffOption}
						onStaffOptionChange={pick.handleOptionChange}
						renderScheduledChip={showActual ? undefined : pick.renderScheduledChip}
						hint={hint}
						conflictKind={conflict.kind}
						showJobPicker={jobManagementEnabled}
						nurseJobs={selection.nurseJobs}
						jobId={jobId}
						onJobChange={selection.pickJob}
						locationOptions={selection.locationOptions}
						location={location}
						onLocationChange={selection.pickLocation}
						window={window}
						timesVersion={times.seedVersion}
						onTimesChange={times.editWindow}
					/>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<SystemButton buttonVariant="secondary" onClick={onClose} data-testid="quick-add-nurse-cancel-button">
						{t("cancel")}
					</SystemButton>
					<SystemButton
						loading={isAdding || isMoving}
						onClick={handleSave}
						disabled={!nurse || isBlocked || isLocationMissing || isMoveWithoutDestination}
						data-testid="quick-add-nurse-submit-button"
					>
						{movingShift && location !== null ? t("quick.add.move.button", { location }) : t("save")}
					</SystemButton>
				</DialogActions>
			</Dialog>
			<WarningDialog {...warningDialogProps} />
		</>
	);
};
